import React from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { BOURGOGNE } from "@/data";
import { EN } from "@/en";

const fadeUp = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.55, delay: i * 0.06, ease: "easeOut" } }),
};

const find = (title) => BOURGOGNE.find((b) => b.title === title);

const GROUPS = [
  { key: "Patrimoine", items: ["Le château de Tanlay", "Le château d'Ancy-le-Franc"] },
  { key: "Nature et mouvement", items: ["Le canal de Bourgogne", "Promenades à pied et à vélo"] },
  { key: "Vignobles et art de vivre", items: ["Chablis et ses vignobles"] },
];

function Card({ b, i }) {
  if (!b) return null;
  const e = EN.explore.items[b.title] || {};
  return (
    <motion.a href={b.link} target="_blank" rel="noopener noreferrer" data-testid={`explore-${b.title}`}
      initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp} custom={i} className="group block">
      <div className="overflow-hidden rounded-2xl mb-5">
        <img src={b.img} alt={e.alt || e.title} className="w-full h-60 object-cover group-hover:scale-105 transition-transform duration-700" />
      </div>
      <h3 className="font-serif text-2xl text-forest">{e.title}</h3>
      <p className="text-charcoal/70 mt-2 leading-relaxed">{e.text}</p>
      {b.credit && <p className="text-[11px] text-charcoal/40 mt-2">{b.credit}</p>}
      <span className="inline-flex items-center gap-2 mt-3 text-forest text-sm font-medium group-hover:gap-3 transition-all">
        {EN.explore.discover} <span className="text-charcoal/40 normal-case">{EN.explore.inFrench}</span> <ArrowRight className="w-4 h-4" />
      </span>
    </motion.a>
  );
}

export default function BourgogneEn() {
  const t = EN.explore;
  return (
    <div data-testid="page-explore-en" className="py-20 lg:py-28">
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="max-w-2xl mb-16">
          <span className="text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">{t.overline}</span>
          <h1 className="font-serif text-5xl lg:text-6xl font-light mt-5 text-forest">{t.title}</h1>
          <p className="text-charcoal/70 mt-6 text-lg">{t.intro}</p>
        </div>

        <div className="space-y-16">
          {GROUPS.map((grp) => (
            <div key={grp.key} data-testid={`explore-group-${grp.key}`}>
              <h2 className="text-xs font-semibold uppercase tracking-[0.28em] text-brown mb-7 pb-3 border-b border-sand">{t.groups[grp.key]}</h2>
              <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
                {grp.items.map((title, i) => <Card key={title} b={find(title)} i={i} />)}
              </div>
            </div>
          ))}
        </div>

        <p data-testid="explore-disclaimer" className="text-xs text-charcoal/40 mt-12">{t.disclaimer}</p>

        {/* Travel journal card */}
        <Link to="/en/tanlay-travel-journal" data-testid="explore-carnet" className="group block mt-16">
          <motion.div initial="hidden" whileInView="show" viewport={{ once: true }} variants={fadeUp}
            className="grid md:grid-cols-[300px_1fr] gap-8 items-center bg-stone/50 border border-sand rounded-2xl p-8 lg:p-10">
            <div className="overflow-hidden rounded-xl bg-[#eee8d8] p-3 rotate-[-1deg] shadow-md">
              <img src="/images/carnet/ancien-tanlay.jpg" alt="Vintage postcard of Tanlay, general view and canal port" className="w-full aspect-[1.55] object-contain bg-[#e5dfd0]" />
              <p className="text-center font-serif text-sm text-charcoal/70 pt-3 pb-1">Tanlay, general view and canal port</p>
            </div>
            <div>
              <span className="text-xs font-semibold uppercase tracking-[0.28em] text-terracotta">The escapes journal</span>
              <h2 className="font-serif text-3xl lg:text-4xl font-light text-forest mt-4">Travel journal</h2>
              <p className="text-charcoal/70 mt-4 leading-relaxed">
                Explore Tanlay, Noyers and nearby Burgundy through historic postcards, local stories, châteaux, the Burgundy Canal and the women who shaped its history.
              </p>
              <span className="inline-flex items-center gap-2 mt-5 text-forest font-medium group-hover:gap-3 transition-all">
                Step into the journey <ArrowRight className="w-4 h-4" />
              </span>
            </div>
          </motion.div>
        </Link>

        <div data-testid="explore-cta" className="mt-16 bg-forest text-cream rounded-2xl px-8 py-14 lg:px-14 text-center">
          <h2 className="font-serif text-3xl lg:text-4xl font-light">{t.ctaTitle}</h2>
          <p className="text-cream/75 mt-4 max-w-xl mx-auto">{t.ctaText}</p>
          <div className="mt-8 flex flex-wrap gap-3 justify-center">
            <Link to="/en/chez-anne"><Button className="rounded-full bg-cream hover:bg-white text-forest px-7 h-11">{t.ctaChezAnne}</Button></Link>
            <Link to="/en/au-19"><Button className="rounded-full bg-cream hover:bg-white text-forest px-7 h-11">{t.ctaAu19}</Button></Link>
            <Link to="/en/contact" data-testid="explore-cta-choose"><Button variant="outline" className="rounded-full border-cream/50 text-cream bg-transparent hover:bg-cream hover:text-forest px-7 h-11">{t.ctaChoose}</Button></Link>
          </div>
        </div>
      </div>
    </div>
  );
}
